import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Search, Briefcase, Building2, Send, FileText, Columns3, Upload, User, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { queryKeys } from '@core/query/queryKeys';
import { jobService, type JobSortColumn } from '@core/supabase/services/jobService';
import { clientService } from '@core/supabase/services/clientService';
import { applicationService } from '@core/supabase/services/applicationService';
import { Job, JobStatus, Client, JOB_PUBLISH_STATUS_LABELS } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { useToast } from '@shared/hooks/useToast';
import { ListPreviewDrawer } from '@shared/components/ui/ListPreviewDrawer';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { RecordMetaCard } from '@shared/components/ui/RecordMetaCard';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { ConfigureListColumnsModal, colClass } from '@shared/components/ui/ConfigureListColumnsModal';
import { useListColumnVisibility } from '@shared/hooks/useListColumnVisibility';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { downloadJobsCsv } from './jobsListCsv';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { DataTableDeleteConfirmModal } from '@shared/components/ui/DataTableDeleteConfirmModal';
import { SortableTh, EnrichColumnTh } from '@shared/components/ui/SortableTableHeader';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { RowHoverActions } from '@shared/components/ui/RowHoverActions';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';
import { JobNewWizardModal } from './JobNewWizardModal';
import { JobLifecycleModal } from './JobLifecycleModal';
import { orgService } from '@core/supabase/services/orgService';
import { Modal } from '@shared/components/ui/Modal';
import { SmartMatchRedirectCTA } from '@modules/smart-match/SmartMatchRedirectCTA';
import { jdTaxonomySummaryLine } from './jdStructuredTaxonomy';

function jobPreviewInitials(title: string) {
  const t = title.trim();
  if (!t) return '?';
  return t.slice(0, 2).toUpperCase();
}

const JOBS_LIST_COLUMNS: ListColumnCatalogItem[] = [
  { id: 'skills', label: 'Skills', optional: true },
  { id: 'client', label: 'Client' },
  { id: 'status', label: 'Status' },
  { id: 'apps', label: 'Apps (#)' },
  { id: 'opened', label: 'Opened', optional: true },
  { id: 'location', label: 'Location', optional: true },
  { id: 'created', label: 'Created' },
];

const STATUS_BADGE: Record<JobStatus, string> = {
  open:    'badge-open',
  on_hold: 'badge-on_hold',
  filled:  'badge-filled',
  closed:  'badge-closed',
};

export function JobsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('open');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [publishModal, setPublishModal] = useState<{ jobId: string; job: Job } | null>(null);
  const [orgSlug, setOrgSlug] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const [sortBy, setSortBy] = useState<JobSortColumn>('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [jdImportOpen, setJdImportOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [jobRowMenu, setJobRowMenu] = useState<{ x: number; y: number; jobId: string } | null>(null);
  const jobRowMenuRef = useRef<HTMLDivElement>(null);

  const clientIdFromUrl = searchParams.get('clientId') ?? undefined;
  const orgId = user?.orgId;

  const jobsCatalog = useMemo(() => JOBS_LIST_COLUMNS, []);
  const { show, setColumn, reset, visible } = useListColumnVisibility(
    'jobs',
    orgId ?? null,
    jobsCatalog,
  );

  const effectiveClientId = clientIdFromUrl || clientFilter || undefined;
  const jobsListParams = useMemo(
    () => ({
      clientId: effectiveClientId,
      status: statusFilter || undefined,
      search: search || undefined,
      page,
      pageSize,
      sortBy,
      sortAsc,
    }),
    [effectiveClientId, statusFilter, search, page, pageSize, sortBy, sortAsc],
  );

  const handleJobSort = (col: JobSortColumn) => {
    if (sortBy === col) {
      setSortAsc((a) => !a);
    } else {
      setSortBy(col);
      setSortAsc(col === 'created_at' ? false : true);
    }
    setPage(1);
  };

  const clientsQuery = useQuery({
    queryKey: orgId ? queryKeys.clients.list(orgId) : ['clients', '__none'],
    queryFn: async () => {
      const r = await clientService.list(orgId!);
      return (Array.isArray(r) ? r : r.data) as Client[];
    },
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });

  const jobsQuery = useQuery({
    queryKey: orgId ? queryKeys.jobs.list(orgId, jobsListParams) : ['jobs', '__none'],
    queryFn: () =>
      jobService.list(orgId!, jobsListParams) as Promise<{ data: Job[]; total: number }>,
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });

  const appsByJobQuery = useQuery({
    queryKey: orgId ? queryKeys.applications.jobCounts(orgId) : ['applications', '__none', 'jobCounts'],
    queryFn: async () => {
      const apps = await applicationService.list(orgId!, { limit: 2000 });
      const byJob: Record<string, number> = {};
      apps.forEach((a) => {
        if (a.job_id) byJob[a.job_id] = (byJob[a.job_id] ?? 0) + 1;
      });
      return byJob;
    },
    enabled: Boolean(orgId),
  });

  const clients = clientsQuery.data ?? [];
  const jobs = jobsQuery.data?.data ?? [];
  const totalItems = jobsQuery.data?.total ?? 0;
  const appsByJob = appsByJobQuery.data ?? {};
  const loading = Boolean(orgId && jobsQuery.isPending);

  useEffect(() => {
    if (clientIdFromUrl && clientFilter !== clientIdFromUrl) setClientFilter(clientIdFromUrl);
  }, [clientIdFromUrl]);

  useEffect(() => {
    if (!orgId) return;
    orgService.getOrg(orgId).then((o) => {
      if (o?.slug) setOrgSlug(o.slug);
      if (o?.name) setOrgName(o.name);
    });
  }, [orgId]);

  useEffect(() => {
    if (!jobRowMenu) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (jobRowMenuRef.current?.contains(t)) return;
      setJobRowMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [jobRowMenu]);

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = jobs.length > 0 && selectedIds.size > 0 && selectedIds.size < jobs.length;
    }
  }, [jobs.length, selectedIds.size]);

  const handleDeleteJobs = useCallback(
    async (ids: string[]) => {
      if (!orgId) return;
      if (!perms.canManageJobs) {
        showToast({ title: 'You do not have permission to delete jobs.', kind: 'error' });
        return;
      }
      if (ids.length === 0) return;
      try {
        for (const id of ids) {
          await jobService.delete(id);
        }
        setSelectedIds(new Set());
        await queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all(orgId) });
        await queryClient.invalidateQueries({ queryKey: queryKeys.applications.jobCounts(orgId) });
      } catch (e) {
        showToast({ title: e instanceof Error ? e.message : 'Delete failed.', kind: 'error' });
      }
    },
    [orgId, perms.canManageJobs, queryClient],
  );

  const runJobsExport = useCallback(
    async (scope: 'selection' | 'all') => {
      if (!orgId) return;
      try {
        const result = (await jobService.list(orgId, {
          clientId: effectiveClientId,
          status: statusFilter || undefined,
          search: search || undefined,
          page: 1,
          pageSize: 10000,
          sortBy,
          sortAsc,
        })) as { data: Job[]; total: number };
        let rows = result.data;
        if (scope === 'selection') {
          if (selectedIds.size === 0) {
            showToast({ title: 'Select rows to export, or use CSV — all under Operations.', kind: 'error' });
            return;
          }
          const sel = selectedIds;
          rows = rows.filter((j) => sel.has(j.id));
        }
        if (rows.length === 0) {
          showToast({ title: 'Nothing to export for the current filters.', kind: 'error' });
          return;
        }
        downloadJobsCsv(rows, appsByJob);
      } catch (e) {
        showToast({ title: e instanceof Error ? e.message : 'Export failed.', kind: 'error' });
      }
    },
    [orgId, effectiveClientId, statusFilter, search, sortBy, sortAsc, selectedIds, appsByJob],
  );

  return (
    <div>
      {toast}
      <SyncStatusBadge className="mb-3" />
      <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Data', href: '/jobs' }, { label: 'Jobs' }]}
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('jobs')}
            onExport={(fmt, scope) => {
              if (fmt === 'csv') void runJobsExport(scope);
              else listExportNotImplemented('Jobs', fmt, scope);
            }}
            extraActions={[
              {
                id: 'configure',
                label: 'Configure',
                icon: <Columns3 className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setConfigureOpen(true),
              },
              {
                id: 'import',
                label: 'Import',
                icon: <Upload className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => {
                  if (!orgId) return;
                  if (!perms.canManageJobs) {
                    showToast({ title: 'You do not have permission to import jobs.', kind: 'error' });
                    return;
                  }
                  setJdImportOpen(true);
                },
              },
              {
                id: 'rights',
                label: 'Manage Rights',
                icon: <User className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setManageRightsOpen(true),
              },
              {
                id: 'publish',
                label: 'Publish',
                icon: <Send className="vscode-text-secondary" strokeWidth={2} />,
                disabled: selectedIds.size !== 1,
                onClick: () => {
                  const job = jobs.find((j) => j.id === Array.from(selectedIds)[0]);
                  if (job) setPublishModal({ jobId: job.id, job });
                },
              },
            ]}
          />
        }
      />

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
          <input className="input pl-9" placeholder="Search jobs..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="input w-auto" value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setPage(1); }}>
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select className="input w-auto" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as JobStatus | ''); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="on_hold">On Hold</option>
          <option value="filled">Filled</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {loading ? (
        <FullPageSpinner />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No jobs found"
          description="Add a job to start tracking candidate applications."
          action={<Link to="/jobs/new" className="btn-primary"><Plus className="w-4 h-4" /> Add Job</Link>}
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <DataTableToolbar hasTable={true} newLabel="New job" newOnClick={() => setWizardOpen(true)} />
          <DataTableDeleteConfirmModal
            open={bulkDeleteOpen}
            onClose={() => setBulkDeleteOpen(false)}
            entityLabel="jobs"
            count={selectedIds.size}
            bodyExtra="Deleting a job removes its applications and other dependent records tied to this JD (database ON DELETE CASCADE where configured). Requisitions may unlink rather than delete."
            onConfirm={() =>
              void handleDeleteJobs(Array.from(selectedIds)).then(() => setBulkDeleteOpen(false))
            }
            requireTypedConfirm="DELETE"
          />
          {selectedIds.size > 0 && (
            <div className="ggj-data-bulk-bar flex flex-wrap items-center gap-2 px-4 py-2">
              <span className="text-sm font-medium vscode-text-primary">{selectedIds.size} selected</span>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={selectedIds.size !== 1}
                onClick={() => {
                  const id = Array.from(selectedIds)[0];
                  if (id) navigate(`/jobs/${id}`);
                }}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={!perms.canManageJobs}
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={selectedIds.size !== 1}
                onClick={() => {
                  const job = jobs.find((j) => j.id === Array.from(selectedIds)[0]);
                  if (job) setPublishModal({ jobId: job.id, job });
                }}
              >
                <Send className="h-3.5 w-3.5" /> Publish
              </button>
            </div>
          )}
          <div className="overflow-x-auto min-w-0">
          <table className="ggj-data-table min-w-[720px]">
            <thead>
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    ref={headerCheckRef}
                    checked={jobs.length > 0 && selectedIds.size === jobs.length}
                    onChange={() => {
                      if (selectedIds.size === jobs.length) setSelectedIds(new Set());
                      else setSelectedIds(new Set(jobs.map((j) => j.id)));
                    }}
                    className="rounded"
                  />
                </th>
                <SortableTh
                  label="Role"
                  column="title"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleJobSort}
                  className="text-left"
                />
                <th className={colClass(show('skills'), 'text-left px-4 py-3')}>Skills</th>
                <th className={colClass(show('client'), 'text-left px-4 py-3')}>Client</th>
                <SortableTh
                  label="Status"
                  column="status"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleJobSort}
                  className={colClass(show('status'), 'text-left')}
                />
                <th className={colClass(show('apps'), 'text-left px-4 py-3 w-16')}>Apps</th>
                <SortableTh
                  label="Opened"
                  column="opened_at"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleJobSort}
                  className={colClass(show('opened'), 'text-left')}
                />
                <SortableTh
                  label="Location"
                  column="location"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleJobSort}
                  className={colClass(show('location'), 'text-left')}
                />
                <SortableTh
                  label="Created"
                  column="created_at"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleJobSort}
                  className={colClass(show('created'), 'text-left whitespace-nowrap')}
                />
                <EnrichColumnTh />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const jdRoleSummary = jdTaxonomySummaryLine(job);
                return (
                <tr
                  key={job.id}
                  tabIndex={0}
                  className={[
                    selectedIds.has(job.id) ? 'bg-brand-500/10' : '',
                    'cursor-pointer outline-none transition-colors hover:bg-[color-mix(in_srgb,var(--vscode-bg-hover)_65%,transparent)] focus-visible:ring-2 focus-visible:ring-brand-500/40',
                  ].join(' ')}
                  onClick={() => setPreviewJobId(job.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPreviewJobId(job.id);
                    }
                    if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      setPreviewJobId(job.id);
                    }
                    if (e.key === 'ArrowLeft' && previewJobId) {
                      e.preventDefault();
                      setPreviewJobId(null);
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setJobRowMenu({ x: e.clientX, y: e.clientY, jobId: job.id });
                  }}
                >
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(job.id)}
                      onChange={() => {
                        const next = new Set(selectedIds);
                        if (next.has(job.id)) next.delete(job.id);
                        else next.add(job.id);
                        setSelectedIds(next);
                      }}
                      className="rounded vscode-border"
                    />
                  </td>
                  <td className="px-3 py-0">
                    <div className="flex items-center gap-2">
                      <TableAvatar name={job.title} size={20} />
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium vscode-text-primary truncate">{job.title}</p>
                        {jdRoleSummary ? (
                          <p className="ggj-table-sub max-w-[280px] truncate" title={jdRoleSummary}>
                            {jdRoleSummary}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className={colClass(show('skills'), 'px-4 py-3')}>
                    {(job.skills_required ?? []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(job.skills_required ?? []).slice(0, 5).map((s) => (
                          <span
                            key={s}
                            className="px-1.5 py-0.5 rounded text-[11px] font-medium vscode-text-tertiary"
                            style={{ background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)' }}
                          >
                            {s}
                          </span>
                        ))}
                        {(job.skills_required?.length ?? 0) > 5 && (
                          <span className="text-[11px] vscode-text-tertiary">+{(job.skills_required?.length ?? 0) - 5}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm vscode-text-tertiary">—</span>
                    )}
                  </td>
                  <td className={colClass(show('client'), 'px-4 py-3')}>
                    <div className="flex items-center gap-1.5 text-sm vscode-text-secondary">
                      <Building2 className="w-3.5 h-3.5 vscode-text-tertiary flex-shrink-0" />
                      {job.client?.name ?? '—'}
                    </div>
                  </td>
                  <td className={colClass(show('status'), 'px-4 py-3')}>
                    <span className={STATUS_BADGE[job.status]}>
                      {job.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className={colClass(show('apps'), 'px-4 py-3 text-sm vscode-text-secondary')}>{appsByJob[job.id] ?? 0}</td>
                  <td className={colClass(show('opened'), 'px-4 py-3 text-sm vscode-text-secondary')}>
                    {job.opened_at || job.created_at
                      ? formatDateTimeGmt(job.opened_at ?? job.created_at)
                      : '—'}
                  </td>
                  <td className={colClass(show('location'), 'px-4 py-3 text-sm vscode-text-secondary')}>{job.location ?? '—'}</td>
                  <td className={colClass(show('created'), 'px-4 py-3 text-sm vscode-text-tertiary tabular-nums whitespace-nowrap')}>
                    {formatCreatedWhen(job.created_at)}
                  </td>
                  <RowHoverActions />
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            entityLabel="jobs"
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* New Job wizard modal */}
      {user?.orgId && (
        <JobNewWizardModal
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onCreated={(job) => { setWizardOpen(false); navigate(`/jobs/${job.id}`); }}
          orgId={user.orgId}
          userId={user.id}
          prefillRequisition={null}
        />
      )}

      {/* Publish lifecycle modal for selected job */}
      {publishModal && user?.orgId && (
        <JobLifecycleModal
          open={true}
          onClose={() => setPublishModal(null)}
          job={publishModal.job}
          orgId={user.orgId}
          orgSlug={orgSlug}
          orgName={orgName}
          initialAxis="publish"
          onJobUpdated={(updated) => {
            setPublishModal(null);
            /* Refetch jobs list to reflect publish status change */
            void jobsQuery.refetch();
          }}
        />
      )}
      <ConfigureListColumnsModal
        open={configureOpen}
        onClose={() => setConfigureOpen(false)}
        entityLabel="Jobs"
        columns={JOBS_LIST_COLUMNS}
        value={visible}
        onChange={setColumn}
        onReset={reset}
      />
      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={orgId ?? null}
        entityKey="jobs"
        subtitle="Per-user access for the Jobs / JDs list — stored locally per browser; server roles still apply."
      />

      <Modal
        open={jdImportOpen}
        onClose={() => {
          setJdImportOpen(false);
          if (orgId) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all(orgId) });
            void queryClient.invalidateQueries({ queryKey: ['smart-match'] });
          }
        }}
        title="Import job descriptions"
        subtitle="Upload JD files — AI extracts fields and creates draft jobs (same flow as Smart Match → Upload JDs)."
        icon={<FileText className="w-4 h-4" />}
        size="2xl"
      >
        <div className="max-h-[min(72vh,720px)] overflow-y-auto -mx-1 px-1">
          <SmartMatchRedirectCTA
            title="JD import moved to Smart Match"
            subtitle="Upload one JD or many on the Smart Match canvas — the agent extracts fields and creates draft jobs inline."
          />
        </div>
      </Modal>

      {previewJobId ? (
        (() => {
          const job = jobs.find((j) => j.id === previewJobId);
          if (!job) return null;
          return (
            <ListPreviewDrawer
              open
              onClose={() => setPreviewJobId(null)}
              typeLabel="Job"
              title={job.title}
              subtitle={job.client?.name ?? undefined}
              openFullHref={`/jobs/${job.id}`}
            >
              <div className="space-y-4 px-2">
                <RecordHeader
                  avatarInitials={jobPreviewInitials(job.title)}
                  title={job.title}
                  meta={[job.client?.name, job.status].filter(Boolean).join(' · ') || undefined}
                />
                <RecordMetaCard
                  title="Summary"
                  rows={[
                    { label: 'Created', value: formatDateTimeGmt(job.created_at) },
                    { label: 'Client', value: job.client?.name ?? '—' },
                    { label: 'Status', value: job.status },
                    { label: 'Openings', value: job.openings_count != null ? String(job.openings_count) : '—' },
                    { label: 'Applications', value: String(appsByJob[job.id] ?? 0) },
                    {
                      label: 'Publish',
                      value: JOB_PUBLISH_STATUS_LABELS[job.publish_status ?? 'draft'] ?? job.publish_status ?? '—',
                    },
                  ]}
                />
              </div>
            </ListPreviewDrawer>
          );
        })()
      ) : null}

      {jobRowMenu ? (
        <div
          ref={jobRowMenuRef}
          className="fixed z-[160] min-w-[200px] rounded-lg border vscode-border vscode-bg-elevated py-1 shadow-xl"
          style={{ left: jobRowMenu.x, top: jobRowMenu.y }}
          role="menu"
        >
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              setPreviewJobId(jobRowMenu.jobId);
              setJobRowMenu(null);
            }}
          >
            Open preview
          </button>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              navigate(`/jobs/${jobRowMenu.jobId}`);
              setJobRowMenu(null);
            }}
          >
            Open in full page
          </button>
        </div>
      ) : null}
    </div>
  );
}
